import {
    Button,
    ButtonSizes,
    ButtonStyles,
    Dropdown,
    Label,
    MultilineInput,
    RadioGroup,
    TextInput
} from '@silverfort/sf-components';
import { FC, useCallback } from 'react';
import { CreateApplicationFormInput } from '../types/CreateApplicationFormInput';
import { CreateApplicationFormErrors } from '../types/CreateApplicationFormErrors';
import { ApplicationCriticalityOptions } from '../../../common';
import { StaleSettingsOptions } from '../../../common/staleStatus';

export type CreateApplicationFormProps = {
    disabled: boolean;
    errors: CreateApplicationFormErrors;
    form: CreateApplicationFormInput;
    toggleDrawer: () => void;
    updateForm: <K extends keyof CreateApplicationFormInput>(key: K, value: CreateApplicationFormInput[K]) => void;
};

export const CreateApplicationForm: FC<CreateApplicationFormProps> = ({
    disabled,
    errors,
    form,
    toggleDrawer,
    updateForm
}) => {
    const updateStaleAfter = useCallback(
        (value: number | Array<number> | undefined) => {
            const v = Array.isArray(value) ? (value.length ? value[0] : null) : value ?? null;

            updateForm('staleAfter', v);
        },
        [updateForm]
    );

    return (
        <div className="p-6 border rounded-lg flex-col flex flex-1 gap-4">
            <div className="flex flex-row gap-2 items-center">
                <Label className="w-[235px]" mandatory text="Name" />

                <div>
                    <TextInput
                        className="w-[287px]"
                        disabled={disabled}
                        maxLength={255}
                        onChange={(value) => updateForm('name', value)}
                        placeholder="Write name"
                        validationMessage={errors?.name}
                        value={form.name}
                    />
                </div>
            </div>

            <div className="flex flex-row gap-2 items-center">
                <Label className="w-[235px]" text="Description" />

                <MultilineInput
                    className="w-[480px]"
                    disabled={disabled}
                    onChange={(value) => updateForm('description', value)}
                    placeholder="Write description"
                    value={form.description}
                />
            </div>

            <div className="flex flex-row gap-2 items-center">
                <Label
                    className="w-[235px]"
                    text="Stale access setting"
                    tooltipInfo="Specify the timeframe after which a user accessing an application or associated resources will be considered stale."
                />

                <Dropdown
                    items={StaleSettingsOptions}
                    onChange={updateStaleAfter}
                    selectedItems={form.staleAfter ?? undefined}
                    variant="trigger"
                />
            </div>

            <div className="flex flex-row gap-2 items-center">
                <Label className="w-[235px]" mandatory text={`Resources`} />

                <Button
                    text="Add Resources"
                    buttonSize={ButtonSizes.Small}
                    buttonStyle={ButtonStyles.Primary}
                    disabled={disabled}
                    onClick={toggleDrawer}
                />

                <span className={`text-red-500 text-10 ${errors.resources ? 'visible' : 'invisible'} h-4 `}>
                    {errors.resources}
                </span>

                {form.selectedIps.length + form.selectedHostnames.length > 0 && (
                    <div className="flex items-center bg-sf-primary-100 text-sf-primary-700 px-2 h-6 rounded text-12">
                        {`${form.selectedIps.length + form.selectedHostnames.length} Resources selected`}
                    </div>
                )}
            </div>

            <div className="flex flex-row gap-2 items-center">
                <Label className="w-[235px]" text="Business criticality" />

                <RadioGroup
                    disabled={disabled}
                    onChange={(value) => updateForm('critically', value as string)}
                    options={ApplicationCriticalityOptions}
                    selectedOption={form.critically}
                />
            </div>
        </div>
    );
};
